import {
  addDays,
  addMinutes,
  differenceInCalendarDays,
  format,
  fromUnixTime,
  isBefore,
  isDate,
  isSameDay,
  parse,
  parseISO,
} from 'date-fns';

import { convertMetric, unitLabel } from './unitConverter';

/**
 * Generate available times
 *
 * @param {string} start - start time (format: 'HH:mm')
 * @param {string} end - end time (format: 'HH:mm')
 * @param {number} defaultTime - default service time (minutes)
 * @param {number} [intervalInMinutes=0] - interval (minutes)
 * @returns {string[]} formatted time array
 */
export const generateAvailableTimes = ({
  start,
  end,
  defaultTime,
  intervalInMinutes = 0,
}: {
  start: string;
  end: string;
  defaultTime: number;
  intervalInMinutes: number;
}) => {
  // Validate required parameters
  if (!start || !end || !defaultTime) return [];

  // Parse service time and interval
  const serviceTime = defaultTime;
  const interval = intervalInMinutes === 0 ? serviceTime : intervalInMinutes;

  // Create base date object (use current date as base)
  const baseDate = new Date();

  // Parse start and end time
  const startTime = parse(start, 'HH:mm', baseDate);
  const endTime = parse(end, 'HH:mm', baseDate);

  // Calculate service end time
  let currentStart = startTime;
  let currentEnd = addMinutes(currentStart, serviceTime);

  const availableTimes: string[] = [];

  // Generate time periods
  while (
    isBefore(currentEnd, endTime) ||
    (currentEnd.getHours() === endTime.getHours() &&
      currentEnd.getMinutes() === endTime.getMinutes())
  ) {
    // Format time (e.g. "9:30 AM")
    availableTimes.push(format(currentStart, 'h:mm a'));

    // Move to next time period
    currentStart = addMinutes(currentStart, interval);
    currentEnd = addMinutes(currentEnd, interval);
  }

  return availableTimes;
};

/**
 * Get ordinal suffix
 *
 * @param {number} num - number
 * @returns {string} ordinal suffix
 */
export const getOrdinalSuffix = (num: number) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

/**
 * Extract all error messages from nested validation errors (e.g., Formik errors)
 *
 * @param {any} errors - validation errors object that may contain nested structures
 * @returns {string[]} array of error message strings
 */
export const extractErrorMessages = (errors: any): string[] => {
  const messages: string[] = [];

  const traverse = (obj: any) => {
    if (typeof obj === 'string') {
      messages.push(obj);
    } else if (Array.isArray(obj)) {
      obj.forEach((item) => {
        if (item !== null && item !== undefined) {
          traverse(item);
        }
      });
    } else if (obj && typeof obj === 'object') {
      Object.values(obj).forEach((value) => {
        if (value !== null && value !== undefined) {
          traverse(value);
        }
      });
    }
  };

  traverse(errors);
  return messages;
};

/**
 * Get start of day UTC time
 *
 * @param {Date} date - date
 * @returns {number} start of day UTC time
 */
export const getStartOfDayUTCTime = (date: Date): number => {
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
  );
  return Math.floor(utcDate.getTime() / 1000);
};

/**
 * Get end of day UTC time
 *
 * @param {Date} date - date
 * @returns {number} end of day UTC time
 */
export const getEndOfDayUTCTime = (date: Date): number => {
  const utcDate = new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999
    )
  );
  return Math.floor(utcDate.getTime() / 1000);
};

/**
 * Normalize unix timestamp input (ms/seconds/ISO) into seconds.
 *
 * @param value - unix timestamp input (ms/seconds/ISO)
 * @returns normalized unix timestamp in seconds
 */
export const normalizeUnix = (value?: number | string | null) => {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/^\d+$/.test(trimmed)) {
      const num = parseInt(trimmed, 10);
      if (!num || Number.isNaN(num)) return 0;
      return num > 1e12 ? Math.floor(num / 1000) : num;
    }
    const parsed = Date.parse(trimmed);
    if (Number.isNaN(parsed)) return 0;
    return Math.floor(parsed / 1000);
  }
  if (!value || Number.isNaN(value)) return 0;
  return value > 1e12 ? Math.floor(value / 1000) : value;
};

/**
 * Convert a UTC timestamp to the local day's midnight timestamp (seconds).
 *
 * @param unixSeconds - UTC timestamp in seconds
 * @returns local day's midnight timestamp in seconds
 */
export const getConvertedDate = (unixSeconds: number) => {
  const date = new Date(unixSeconds * 1000);
  const localMidnight = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  );
  return Math.floor(localMidnight.getTime() / 1000);
};

export const safeString = (value?: unknown) =>
  value == null ? '' : String(value);

export const toNumber = (value?: number | string | null) => {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  const trimmed = value.trim();
  if (!trimmed) return 0;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const secondsToTime = (
  value?: number | string | null,
  short = false
) => {
  const totalSeconds = parseInt(String(value ?? 0), 10);
  if (Number.isNaN(totalSeconds)) {
    return short ? '--:--' : '--:--:--';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds - hours * 3600) / 60);
  const seconds = totalSeconds - hours * 3600 - minutes * 60;

  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  return short ? `${mm}:${ss}` : `${hh}:${mm}:${ss}`;
};

export const formatReps = (value?: number | string | null) => {
  if (value == null) return '--';
  return String(value)
    .replace(/(^-1$)/g, 'MAX')
    .replace(/,-1/g, `,MAX`)
    .replace(/-1,/g, `MAX,`);
};

export const formatMeasurementValue = (
  value?: number | string | Array<number | string> | null
) => {
  if (value == null || value === '') return '--';
  if (Array.isArray(value)) {
    if (value.length === 0) return '--';
    const formatted = value.map((entry) =>
      entry == null || entry === '' ? '--' : String(entry)
    );
    if (value.every((entry) => entry == value[0])) {
      return formatted[0];
    }
    return formatted.join(',');
  }
  return String(value);
};

export const convertUnitValue = (
  value?: number | string | null,
  measurement?: string,
  usesMetric?: boolean,
  decimalPlaces?: number
) => {
  if (value == null) return '--';
  const normalized = safeString(value);
  if (!normalized) return '--';

  if (normalized.includes(',')) {
    return normalized
      .split(',')
      .map((entry) =>
        convertMetric(
          toNumber(entry),
          measurement || '',
          usesMetric,
          decimalPlaces
        )
      )
      .join(', ');
  }

  return convertMetric(
    toNumber(value),
    measurement || '',
    usesMetric,
    decimalPlaces
  );
};

export const formatDistance = (
  value?: number | string | null,
  measurement?: string,
  usesMetric?: boolean
) => {
  const converted = convertUnitValue(value, measurement, usesMetric, 2);
  if (converted === '--') {
    const raw = value == null ? '--' : String(value);
    const unit = measurement || '';
    return `${raw} ${unit}`.trim() || '--';
  }
  const label = unitLabel(measurement || '', usesMetric);

  return `${converted} ${label}`.trim();
};

export const formatWeight = (
  value?: number | string | null,
  usesMetric?: boolean,
  decimalPlaces = 2
) => {
  const converted = convertUnitValue(value, 'lbs', usesMetric, decimalPlaces);
  if (converted === '--') return '--';
  const label = unitLabel('lbs', usesMetric);
  return `${converted} ${label}`.trim();
};

export const getRepMaxWeights = (
  exercise: any,
  user: any,
  usesMetric?: boolean
) => {
  if (!exercise || !exercise.repMaxExerciseId || !exercise.repMaxPercentages) {
    return '';
  }
  const repMaxes = user?.repMaxes || [];
  const repMax = repMaxes.find(
    (repMaxEntry: any) =>
      safeString(repMaxEntry.exerciseId) ===
        safeString(exercise.repMaxExerciseId) &&
      safeString(repMaxEntry.reps) === safeString(exercise.repMaxCount)
  );
  if (!repMax) return '';

  const sets = parseInt(String(exercise.sets || 0), 10);
  const values: string[] = [];
  for (let index = 0; index < sets; index += 1) {
    const percentageEntry = exercise.repMaxPercentages?.[index];
    const percentage =
      typeof percentageEntry === 'number'
        ? percentageEntry
        : Number(percentageEntry?.value ?? percentageEntry);
    if (!Number.isFinite(percentage) || percentage <= 0) continue;
    const baseWeight = Number(repMax.weight || 0);
    const weight = Math.round(baseWeight * (Number(percentage) / 100));
    const converted = convertMetric(weight, 'lbs', usesMetric, 0);
    values.push(String(converted));
  }
  return values.join(', ');
};

export const parseTimestamp = (value?: number | string | null) => {
  if (value === null || value === undefined) return null;
  if (isDate(value)) return value as Date;
  if (typeof value === 'number') return fromUnixTime(value);
  const trimmed = value.trim();
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  if (!Number.isNaN(numeric)) return fromUnixTime(numeric);
  try {
    return parseISO(trimmed);
  } catch (_error) {
    return null;
  }
};

export const formatMinutesSeconds = (value?: number | string | null) => {
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '00:00';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const formatNumber = (value?: number | string | null, decimals = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Match v2's workout/score-summary helper.
 *
 * @param workout - workout object with score fields
 * @param options.hideLabel - hide the label prefix
 * @param options.usesMetric - metric flag for unit conversions
 */
export const scoreSummary = (
  workout: {
    scoreType?: string | null;
    scoreTypeLabel?: string | null;
    scoreValue?: number | string | null;
    scoreValueTwo?: number | string | null;
  },
  options: { hideLabel?: boolean; usesMetric?: boolean } = {}
) => {
  const { hideLabel = false, usesMetric } = options;
  const type = workout?.scoreType;
  const label = workout?.scoreTypeLabel ? `${workout.scoreTypeLabel}: ` : '';

  if (type === 'time') {
    return `${hideLabel ? '' : label}${secondsToTime(workout?.scoreValue)}`;
  }

  if (type === 'rounds_reps') {
    const rounds = formatNumber(workout?.scoreValue);
    const reps = formatNumber(workout?.scoreValueTwo);
    return `${rounds} rounds, ${reps} reps`;
  }

  if (type === 'total_weight') {
    const value = convertMetric(
      toNumber(workout?.scoreValue),
      'lbs',
      usesMetric
    );
    const unit = unitLabel('lbs', usesMetric);
    return `${value} ${unit}`.trim();
  }

  const value = formatNumber(workout?.scoreValue);
  return `${hideLabel ? '' : label}${value}`;
};

export const getCalendarLabel = (unixSeconds: number) => {
  const selected = fromUnixTime(unixSeconds);
  const today = new Date();
  if (isSameDay(selected, today)) return 'Today';
  if (isSameDay(selected, addDays(today, -1))) return 'Yesterday';
  if (isSameDay(selected, addDays(today, 1))) return 'Tomorrow';

  const diff = differenceInCalendarDays(selected, today);
  if (diff < -1 && diff >= -6) {
    return `Last ${format(selected, 'EEEE')}`;
  }
  if (diff > 1 && diff <= 6) {
    return format(selected, 'EEEE');
  }
  return format(selected, 'MM/dd/yyyy');
};
